import React, { Fragment, ReactElement, useEffect, useState, ChangeEvent } from "react";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead, TablePagination,
  TableRow,
  TableSortLabel
} from "@mui/material";
import { DateTime } from "luxon";
import {
  BaseType,
  IDelayBase,
  IDelayMeltShop,
  IDictionary,
  IParentSize,
  ITreeCategoryNode,
  NullValue
} from "../../types";
import {
  formatDate,
  formatDateTimeWithSeconds,
  formatNumber,
  sortText,
  stableSort
} from "../../system/utils";
import EditDelayDialog from "./dialogs/editDelayDialog";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CutDelayDialog from "./dialogs/cutDelayDialog";
import { ICategoryProcessed } from "./utils";
import { FilterList } from "@mui/icons-material";
import FilterDialog from "./dialogs/filterDialog";
import { useAsyncFetch } from "../../system/useFetch";
import TableContainer from "../../components/tableContainer";
import TablePaginationActions from "../../components/tablePaginationActions";

type ColumnType = keyof IDelayMeltShop | "factoryDay" | "shift" | "crew" | "machine" | "path" | "operationEdit";

interface IColumn {
  id: ColumnType;
  label: string;
  minWidth: (flag?: boolean) => number;
  width?: (flag?: boolean) => number;
  align?: "right";
  format?: (value: number) => string;
  filter?: boolean;
  prodField?: boolean;
}

type Order = "asc" | "desc";

const columns: IColumn[] = [
  {
    id: "factoryDay",
    label: "Factory Day",
    minWidth: () => 100,
    width: () => 140,
    format: (value) => DateTime.fromSeconds(value).toLocaleString(DateTime.DATE_SHORT),
    prodField: true
  },
  {
    id: "shift",
    label: "Shift",
    minWidth: () => 80,
    width:() => 80,
    prodField: true
  },
  {
    id: "crew",
    label: "Crew",
    minWidth: () => 80,
    width: () => 80,
    prodField: true
  },
  {
    id: "machine",
    label: "Machine",
    minWidth: () => 100,
    width: () => 100,
    prodField: true
  },
  {
    id: "beginDatetime",
    label: "Begin",
    minWidth: () => 170,
    width: () => 190,
    format: (value) => formatDateTimeWithSeconds(value)
  },
  {
    id: "endDatetime",
    label: "End",
    minWidth: () => 170,
    width: () => 190,
    format: (value) => formatDateTimeWithSeconds(value)
  },
  {
    id: "duration",
    label: "Duration [min]",
    minWidth: () => 150,
    width: () => 150,
    align: "right"
  },
  {
    id: "path",
    label: "Path",
    minWidth: () => 150,
    filter: true
  },
  {
    id: "category",
    label: "Category",
    minWidth: () => 150,
  },
  {
    id: "comment",
    label: "Justification",
    minWidth: () => 100,
  },
  {
    id: "editedBy",
    label: "Edited By",
    minWidth: () => 150,
    width: () => 150,
  },
  {
    id: "operationEdit",
    label: "",
    minWidth: () => 90,
    width: () => 90,
  },
];

interface IInterruptionTableProps {
  data: IDelayMeltShop[];
  selected: IDelayBase | null;
  setSelected: (row: IDelayBase) => void;
  categoryProcessed: ICategoryProcessed;
  onRefresh?: any;
  onRefreshAfterCut?: any;
  isEditable?: boolean | null;
  parentSize: IParentSize;
}

function descendingComparator(a: IDelayMeltShop, b: IDelayMeltShop, orderBy: ColumnType, categoryProcessed: ICategoryProcessed) {
  if (orderBy === "factoryDay") {
    if (a.production && b.production) {
      if (b.production.shiftHistory.factoryDay < a.production.shiftHistory.factoryDay) {
        return -1;
      }

      return b.production.shiftHistory.factoryDay > a.production.shiftHistory.factoryDay ? 1 : 0;
    }

    return 0;
  }

  if (orderBy === "shift") {
    if (a.production && b.production) {
      if (b.production.shiftHistory.name < a.production.shiftHistory.name) {
        return -1;
      }

      return b.production.shiftHistory.name > a.production.shiftHistory.name ? 1 : 0;
    }

    return 0;
  }

  if (orderBy === "crew") {
    if (a.production && b.production) {
      if (b.production.crew.name < a.production.crew.name) {
        return -1;
      }

      return b.production.crew.name > a.production.crew.name ? 1 : 0;
    }

    return 0;
  }

  if (orderBy === "machine") {
    if (a.production && b.production) {
      if (b.production.zone.name < a.production.zone.name) {
        return -1;
      }

      return b.production.zone.name > a.production.zone.name ? 1 : 0;
    }

    return 0;
  }

  if (orderBy === "beginDatetime") {
    if (b.beginDatetime < a.beginDatetime) {
      return -1;
    }

    return b.beginDatetime > a.beginDatetime ? 1 : 0;
  }

  if (orderBy === "endDatetime" && b.endDatetime && a.endDatetime) {
    if (b.endDatetime < a.endDatetime) {
      return -1;
    }

    return b.endDatetime > a.endDatetime ? 1 : 0;
  }

  if (orderBy === "duration" && b.duration && a.duration) {
    if (b.duration < a.duration) {
      return -1;
    }

    return b.duration > a.duration ? 1 : 0;
  }

  if (orderBy === "path" && b.category && a.category) {
    const pathA = categoryProcessed.dictionary[a.category.categoryID] ? categoryProcessed.dictionary[a.category.categoryID].path.text : "";
    const pathB = categoryProcessed.dictionary[b.category.categoryID] ? categoryProcessed.dictionary[b.category.categoryID].path.text : "";

    if (pathB < pathA) {
      return -1;
    }

    return pathB > pathA ? 1 : 0;
  }

  return 0;
}


function getComparator<Key extends ColumnType>(
  order: Order,
  orderBy: Key,
  categoryProcessed: ICategoryProcessed
): (a: IDelayMeltShop, b: IDelayMeltShop) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy, categoryProcessed)
    : (a, b) => -descendingComparator(a, b, orderBy, categoryProcessed);
}

const InterruptionTable = ({ data, selected, setSelected, onRefresh, onRefreshAfterCut, isEditable, categoryProcessed, parentSize }: IInterruptionTableProps): ReactElement => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<ColumnType>("beginDatetime");
  const [open, setOpen] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [openCutDialog, setOpenCutDialog] = useState<boolean>(false);
  const [selectedNewRow, setSelectedNewRow] = useState({} as IDelayMeltShop);
  const [updateDelay] = useAsyncFetch();
  const [insNewDelay] = useAsyncFetch<IDelayMeltShop>();
  const [filter, setFilter] = useState<IDictionary<string[]> | null>(null);
  const [dataFiltered, setDataFiltered] = useState<IDelayMeltShop[] | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const tableContainerHeight = parentSize.height - 50;

  const keys =  Object.keys(categoryProcessed.keywordMap).sort(sortText);
  const dataLength = data ? data.length : 0;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataLength) : 0;

  useEffect(() => {

    const newObj: IDictionary<string[]> = {};
    keys.forEach(key => newObj[key] = [ NullValue ]);
    setFilter(newObj);
    setDataFiltered(data);
  }, [ categoryProcessed, data ]);

  const handleAccept = async (delayHistoryID: number, category: ITreeCategoryNode, comment: string, editedBy: string) => {

    await updateDelay({ module: "delay", query: "updateDelaysHistoryById", payload: { qmos: false, delayHistoryID, categoryID: category.categoryID, comment, editedBy } });
    // selectedNewRow.comment = comment;
    onRefresh(delayHistoryID, category, comment, editedBy);
    setOpen(false);
  };

  const createSortHandler = (property: ColumnType) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, row: IDelayMeltShop) => {
    setSelected(row);
  };

  const handleEditButton2 = (row: IDelayMeltShop) => {
    setSelectedNewRow(row);
    setOpen(true);
  };


  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenCutDialog = (row: IDelayMeltShop) => {
    setSelectedNewRow(row);
    setOpenCutDialog(true);
  };

  const handleCutDialogAccept = async (productionID: number | null, machineID: number, beginD: number, endD: number, delayHistoryID: number) => {

    const res = await insNewDelay({ module: "delay", query: "insDelaysHistory",
      payload: {
        qmos: false,
        beginDatetime: beginD / 1000,
        endDatetime: endD / 1000,
        productionID,
        machineID,
        delayHistoryID
      } } );

    if(res) {
      onRefreshAfterCut(res, delayHistoryID, beginD / 1000);
    }
    setOpenCutDialog(false);

  };

  const handleCloseCutDialog = () => {
    setOpenCutDialog(false);
  };

  const onFilterClick = () => {
    setOpenFilter(true);
  };

  const handleFilterClose = () => {
    setOpenFilter(false);
  };

  const onHandleFilterAccept = (newFilter: IDictionary<string[]>) => {
    const newData: IDelayMeltShop[] = [];

    data.forEach(delay => {
      const category = categoryProcessed.dictionary[delay.category.categoryID];
      if (category) {
        // keys.find(k => newFilter[k].find(v => === v && v !== NullValue))
        let counter = keys.length;
        keys.forEach(key => {
          if (newFilter[key][0] === NullValue || newFilter[key].find(x => category.path.keyword[key] === x)) {
            counter--;
          }
        });

        if (counter === 0) {
          newData.push(delay);
        }
      }
    });

    setDataFiltered(newData);
    setOpenFilter(false);
    setFilter(newFilter);
  };


  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filterApplied = filter && keys.find(key => filter[key] && filter[key][0] !== NullValue);
  const hasProd = dataFiltered !== null && dataFiltered.length > 0 && dataFiltered[0].production !== null;

  return (
    <Fragment>
      <TableContainer style={{ height: tableContainerHeight, maxHeight: tableContainerHeight }}>
        <Table stickyHeader={true} size="small">
          <TableHead>
            <TableRow>
              {columns.filter(x => hasProd || !x.prodField).map((column) =>
                <TableCell
                  className="table-header"
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth(hasProd), width: column.width ? column.width(hasProd) : undefined }}
                  sortDirection={orderBy === column.id ? order : false}>
                  <div className="display-flex-row">
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={createSortHandler(column.id as keyof IDelayMeltShop)}>
                      {column.label}
                      {orderBy === column.id
                        ? <span className="cell-visuallyHidden">
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </span>
                        : null}
                    </TableSortLabel>

                    {column.filter &&
                      <IconButton onClick={onFilterClick} >
                        <FilterList fontSize="small" color={filterApplied ? "secondary" : "inherit"}/>
                      </IconButton>
                    }
                  </div>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {/*{dataFiltered && stableSort<any>(dataFiltered, getComparator(order, orderBy, categoryProcessed)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: IDelay, index: number) => {*/}
            {dataFiltered && stableSort<any>(dataFiltered, getComparator(order, orderBy, categoryProcessed))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: IDelayMeltShop, index: number) => {
                return (
                  <TableRow hover={true} role="checkbox" tabIndex={-1} key={`alarm-row-${index}`}
                    onClick={(event) => handleClick(event, row)}
                    selected={row === selected}>
                    {columns.filter(x => hasProd || !x.prodField).map((column: IColumn) => {
                      if (column.id === "duration") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell">
                            {row.duration && formatNumber(row.duration / 60, 2)}
                          </TableCell>
                        );
                      }

                      if (column.id === "category") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell">
                            {row.category.name}
                          </TableCell>
                        );
                      }

                      if (column.id === "factoryDay") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell">
                            {row.production ? formatDate(row.production.shiftHistory.factoryDay) : "---"}
                          </TableCell>
                        );
                      }

                      if (column.id === "shift") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell">
                            {row.production ? row.production.shiftHistory.name : "---"}
                          </TableCell>
                        );
                      }

                      if (column.id === "crew") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell">
                            {row.production ? row.production.crew.name : "---"}
                          </TableCell>
                        );
                      }

                      if (column.id === "machine") {
                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align} className="table-cell">
                            {row.production ? row.production.machine.name : "---"}
                          </TableCell>
                        );
                      }

                      if (column.id === "path") {
                        const path = categoryProcessed.dictionary[row.category.categoryID] ? categoryProcessed.dictionary[row.category.categoryID].path.text : null;

                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align}  className="table-cell">
                            {path}
                          </TableCell>
                        );
                      }

                      // if (column.id === "beginDatetime") {
                      //   return (
                      //     <TableCell key={`${column.id}-${index}`} align={column.align}>
                      //       {row.production ? formatTimeWithSeconds(row[column.id]) : formatDateTimeWithSeconds(row[column.id])}
                      //     </TableCell>
                      //   );
                      // }
                      //
                      // if (column.id === "endDatetime") {
                      //   return (
                      //     <TableCell key={`${column.id}-${index}`} align={column.align}>
                      //       {row.production ? formatTimeWithSeconds(row[column.id]) : formatDateTimeWithSeconds(row[column.id])}
                      //     </TableCell>
                      //   );
                      // }

                      // si la columna es editable se dibujan

                      if (column.id === "operationEdit") {
                        if(isEditable){
                          return (
                            <TableCell key={`${column.id}-${index}`} align={column.align}  className="table-cell">

                              {row.production &&
                              <IconButton aria-label="edit" size={"small"} onClick={() => handleOpenCutDialog(row)}>
                                <CallSplitIcon/>
                              </IconButton>
                              }

                              <IconButton aria-label="edit" size={"small"} onClick={() => handleEditButton2(row)}>
                                <EditIcon/>
                              </IconButton>

                            </TableCell>
                          );
                        }

                        return null;
                      }

                      const value = row[column.id] as BaseType;

                      return (
                        <TableCell key={`${column.id}-${index}`} align={column.align}  className="table-cell">
                          {column.format && typeof value === "number" ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            {emptyRows > 0 &&
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6}/>
              </TableRow>
            }

            <TableRow>
              {!dataFiltered &&
                <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2"  >
                  <CircularProgress className=""/>
                </Paper>
              }
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
              colSpan={columns.length + 1}
              count={dataLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableBody>
      </Table>

      <EditDelayDialog open={open} handleClose={handleClose} selectedNewRow={selectedNewRow}
        handleAccept={handleAccept} categoryProcessed={categoryProcessed}/>

      <CutDelayDialog open={openCutDialog} handleClose={handleCloseCutDialog} selectedNewRow={selectedNewRow}
        handleAccept={handleCutDialogAccept}/>

      { filter &&
        <FilterDialog open={openFilter} handleClose={handleFilterClose} onHandleAccept={onHandleFilterAccept}
          keywordMap={categoryProcessed.keywordMap} currentFilter={filter}/> }
    </Fragment>
  );
};

export default InterruptionTable;
